// DokumentEditor — editor ŽIVÉHO dokumentu (ústava 8.A + 67.14). Mirko 2026-07-23.
// Split adresát · auto značka KAMIR/ROK/NNN · draft→regenerovať→odoslať→ZÁMOK · verzie.
// Ukladá cez /api/doc-archive (draft-on-first-save = žiadne diery v číslovaní od zrušených).
// DOKUMENTY_VYTVARANIE_V1 (Mirko 2. 11. 2026, mockup 5004, zadania matrix 5002–5009): vytváranie prerobené —
// „Čo vybavuješ?“ (jeden výber = druh + predloha + predmet), Komu = partner z registra (adresa z buniek karty, ústava 23.Z.1)
// a kontaktná osoba z Kontaktov (blok KontaktnaOsoba, ústava 131.10.A/G — e-mail sa vyberá, nie je pevný), pri dokumente
// pre zamestnanca výber zamestnanca z číselníka, náhľad na originálnom hlavičkovom papieri, čas v hodinách a minútach.
// Volajúci (archív Dokumentov, Zákazka, Reklamácie, karta zamestnanca, sprievodca) posielajú ten istý context ako doteraz.
import { setDirty as setGuardDirty } from '../hooks/unsavedGuard.js'   // p97 — doklad = obrazovka (ústava 67.15)
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { Modal } from './Modal.jsx'
import { Btn, BtnGhost } from './Btn.jsx'
import { Input } from './Input.jsx'
import { SearchableSelect } from './Ui.jsx'
import { useToast } from './Toast.jsx'
import { useAuth } from '../auth/AuthContext.jsx'
import { fetchAuth } from '../auth/fetchAuth.js'
import { COLORS, SPACE, RADIUS, todayLocal } from '../theme.js'
import DOMPurify from 'dompurify'
import { DOC_VZORY } from './docTemplates.js'
import { KontaktnaOsoba } from './KontaktyPartnera.jsx'
import NoviCustomerDialog from './NoviCustomerDialog.jsx'

// WYSIWYG (Mirko 2026-07-23): telo dokumentu = bezpečné HTML. Povolené len formátovacie tagy, žiadne atribúty.
const CE_ALLOWED = { ALLOWED_TAGS: ['p', 'div', 'br', 'b', 'strong', 'i', 'em', 'u', 'ul', 'ol', 'li', 'span', 'table', 'tbody', 'tr', 'td', 'small', 'sup'], ALLOWED_ATTR: ['class'] }  // small+sup+class: formularove predlohy (Mirko 29.7.) — musi sediet s _WYS_TAGS/_WYS_CLASSES v letterhead_pdf.js
const cleanHtml = (html) => DOMPurify.sanitize(String(html || ''), CE_ALLOWED)
const _escH = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
// Starý dokument / vzor v čistom texte (s **markermi**) → HTML pre editor
const textToHtml = (t) => (String(t || '').split(/\n\s*\n/).map(p => '<p>' + _escH(p).replace(/\n/g, '<br>').replace(/\*\*([^*\n]+)\*\*/g, '<b>$1</b>').replace(/(^|[^\w])_([^_\n]+?)_(?=[^\w]|$)/g, '$1<i>$2</i>') + '</p>').join('')) || '<p></p>'
const isHtml = (s) => /<(p|div|br|ul|ol|li|b|strong|i|em|u|span|table)\b/i.test(String(s || ''))
const bodyToEditorHtml = (body) => cleanHtml(isHtml(body) ? String(body) : textToHtml(body))
const htmlHasText = (html) => String(html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').trim().length > 0

const DOC_TYPES = ['Oznámenie', 'List', 'Stanovisko', 'Zápisnica', 'Sprievodný list', 'Výzva', 'Splnomocnenie', 'Čestné vyhlásenie', 'Potvrdenie', 'Reklamácia', 'Protokol', 'Dohoda', 'Záznam', 'Súhlas', 'Zmluva', 'Dodatok']   // zmluvy_typy_v1
const VZORY = DOC_VZORY
// DOKUMENTY_VYTVARANIE_V1: „Čo vybavuješ?“ — v:<kľúč predlohy> alebo t:<druh bez predlohy>. Predloha, ktorá tu chýba, sa ukáže v Ostatné.
const DRUHY = [
  ['Zákazka a partner', [['t:List', 'List'], ['v:vyzva', 'Výzva na úhradu'], ['v:oznamenie', 'Oznámenie'], ['v:vratenie_faktury', 'Vrátenie faktúry'], ['v:potvrdenie', 'Potvrdenie o prevzatí'],
    ['t:Stanovisko', 'Stanovisko'], ['t:Zápisnica', 'Zápisnica'], ['t:Sprievodný list', 'Sprievodný list'], ['t:Čestné vyhlásenie', 'Čestné vyhlásenie'], ['t:Reklamácia', 'Reklamácia']]],
  ['Zamestnanec', [['v:potvrdenie_cinnosti_vodica', 'Dovolenkový list vodiča'], ['v:bozp', 'BOZP a požiarna ochrana'], ['v:gdpr', 'GDPR — súhlas'], ['v:hmotna_zodpovednost', 'Hmotná zodpovednosť'], ['v:naradie_protokol', 'Náradie — protokol']]],
  ['Vozidlá a úrady', [['v:spl_vozidlo', 'Plná moc — vozidlo'], ['v:spl_di', 'Plná moc — dopravný inšpektorát'], ['v:spl_cudzinci', 'Plná moc — cudzinecká polícia'], ['v:spl_vseobecne', 'Plná moc — všeobecná'], ['v:vozidlo_protokol', 'Protokol odovzdania vozidla']]],
]
const _vDruhoch = new Set(DRUHY.flatMap(([, l]) => l.map(([k]) => k)))
DRUHY.push(['Ostatné', [
  ...VZORY.filter(v => !_vDruhoch.has('v:' + v.key)).map(v => ['v:' + v.key, v.label]),
  ...['Výzva', 'Splnomocnenie', 'Potvrdenie', 'Protokol', 'Dohoda', 'Záznam', 'Súhlas', 'Zmluva', 'Dodatok'].map(t => ['t:' + t, t]),
]])
const bezDiak = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
const todayISO = () => todayLocal()
const lbl = { fontSize: 11, fontWeight: 700, color: COLORS.muted, marginBottom: 4, display: 'block' }
const fld = { width: '100%', padding: '8px 10px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit' }
const fbtn = { fontSize: 12, padding: '3px 10px', border: `1px solid ${COLORS.line}`, background: COLORS.bgSoft, borderRadius: RADIUS.sm, cursor: 'pointer', fontFamily: 'inherit', color: COLORS.text }
const karta = { background: '#fff', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md || 8, padding: '10px 12px' }
const nadpis = (c, t, popis) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
    <span style={{ width: 18, height: 18, borderRadius: '50%', background: COLORS.nav, color: '#fff', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 18px' }}>{c}</span>
    <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '.05em' }}>{t}</span>
    {popis && <span style={{ fontSize: 11, color: COLORS.muted }}>{popis}</span>}
  </div>
)
// adresa partnera z buniek karty (ústava 23.Z.1); starý partner bez buniek → riadok „ulica číslo, PSČ obec“
const adresaPartnera = (c) => {
  if (!c) return { ulica: '', psc_mesto: '' }
  const ul = [c.adresa_ulica, c.adresa_cislo].map(x => String(x || '').trim()).filter(Boolean).join(' ')
  const psc = String(c.adresa_psc || '').replace(/^(\d{3})\s*(\d{2})$/, '$1 $2')
  const obec = [psc, c.adresa_obec].map(x => String(x || '').trim()).filter(Boolean).join(' ')
  const stat = String(c.adresa_stat || '').trim()
  const statT = stat && !/^(sk|svk|slovensko|slovenská republika)$/i.test(stat) ? stat : ''
  if (ul || obec) return { ulica: ul, psc_mesto: [obec, statT].filter(Boolean).join(', ') }
  const casti = String(c.address || '').split(/[,\n]/).map(s => s.trim()).filter(Boolean)
  return { ulica: casti[0] || '', psc_mesto: casti.slice(1).join(', ') }
}
const EMAIL_OK = /^[^@\s]+@[^@\s]+\.[^@\s]+$/
const eur = (n) => (n == null || n === '' || isNaN(Number(n))) ? '' : Number(n).toLocaleString('sk-SK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
// čas a dátum „HH:MM / DD.MM.RRRR“ (dovolenkový list vodiča) — Mirko 2. 11.: „lomitka matrix neberie daj polia do ktorých zadávam hh mm“
const dva = (x) => String(x).padStart(2, '0')
const casDatumZloz = (h, m, d) => {
  if (h === '' || m === '' || !d) return ''
  const p = String(d).split('-'); if (p.length !== 3) return ''
  return dva(h) + ':' + dva(m) + ' / ' + dva(p[2]) + '.' + dva(p[1]) + '.' + p[0]
}
const casDatumChyba = (h, m) => {
  if (h === '' && m === '') return ''
  const hh = Number(h), mm = Number(m)
  if (h === '' || !Number.isInteger(hh) || hh < 0 || hh > 24) return 'hodiny 0 až 24'
  if (m === '' || !Number.isInteger(mm) || mm < 0 || mm > 59) return 'minúty 0 až 59'
  if (hh === 24 && mm !== 0) return '24 len s 00 minútami (koniec dňa)'
  return ''
}
const casDatumRozloz = (s) => {
  const r = /^(\d{1,2}):(\d{2})\s*\/\s*(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{4})$/.exec(String(s || '').trim())
  return r ? { h: String(Number(r[1])), m: r[2], d: r[5] + '-' + dva(r[4]) + '-' + dva(r[3]) } : null
}

const EMPTY = () => ({
  adr_firma: '', adr_do_ruk: '', adr_ulica: '', adr_psc_mesto: '', adr_email: '',
  nasa_znacka: '', vasa_znacka: '', miesto: 'Prešov', datum: todayISO(), doc_type: 'List',
  doc_mode: 'vec', subject: '', body: '', pozdrav: 'S pozdravom', podpis: '',
  project_id: '', dopyt_id: '', images: [], font_size: 'normal', tpl_key: '', tpl_values: {},
})

export default function DokumentEditor({ open, docId = null, context = {}, onClose, onSaved, onPlnaMoc = null }) {   // onPlnaMoc: archív Dokumentov otvorí plnú moc na flotilu (sprievodca, 8.DOK)
  const toast = useToast()
  const { user } = useAuth()
  const [f, setF] = useState(EMPTY())
  const [id, setId] = useState(docId)
  const [locked, setLocked] = useState(false)
  const [status, setStatus] = useState('draft')
  const [docVersion, setDocVersion] = useState(1)
  const [pdfUrl, setPdfUrl] = useState('')
  const [busy, setBusy] = useState(null)
  const [users, setUsers] = useState([])
  const [projects, setProjects] = useState([])
  const [partners, setPartners] = useState([])
  const [zamestnanci, setZamestnanci] = useState([])   // na predvyplnenie predlohy (Mirko 29.7.)
  const [vozidla, setVozidla] = useState([])
  const [dirty, setDirty] = useState(false)
  const [gDlg, setGDlg] = useState(false)
  const [vazby, setVazby] = useState([])   // DOKUMENTY_V1: väzby na zamestnanca/vozidlo (Načítať z Matrixu, sprievodca) → dokument sa ukáže na ich kartách
  // DOKUMENTY_VYTVARANIE_V1
  const [partnerId, setPartnerId] = useState('')        // adresát = partner z registra (väzba customer)
  const [partner0, setPartner0] = useState('')          // partner pri otvorení — väzby na partnera sa menia, len keď ho človek zmení
  const [kontaktTel, setKontaktTel] = useState('')       // telefón kontaktnej osoby — len pre blok, do listu nejde
  const [novyPartner, setNovyPartner] = useState(false)
  const [hladajDruh, setHladajDruh] = useState('')
  const [dosleFa, setDosleFa] = useState({ pre: '', rows: [], chyba: '' })
  const [nahlad, setNahlad] = useState('')
  const [strany, setStrany] = useState(null)
  const [mimo, setMimo] = useState(null)   // DOKUMENTY_DOPLNKY_V1: { sposob, datum } — Odoslané mimo Matrixu
  const [firma, setFirma] = useState(null)   // údaje podniku z Nastavení (company_settings) pre tlačivá
  const set = (k, v) => { setF(p => ({ ...p, [k]: v })); setDirty(true) }
  // p97: dokument sa hlási do spoločného registra neuložených zmien — okno Odoslať a PDF zo zoznamu ho najprv uložia (ústava 67.15)
  useEffect(() => {
    const k = 'dokument'
    setGuardDirty(k, dirty, async () => { try { await ensureSaved(); setDirty(false); return true } catch (_) { return false } })
    return () => setGuardDirty(k, false)
  })
  // WYSIWYG contentEditable (uncontrolled — obsah nastavujeme imperatívne, nerozbije kurzor). Mirko 2026-07-23.
  const ceRef = useRef(null)
  const setCE = (body) => { if (ceRef.current) ceRef.current.innerHTML = bodyToEditorHtml(body) }
  const readCE = () => { if (!ceRef.current) return; const html = cleanHtml(ceRef.current.innerHTML); setF(p => ({ ...p, body: html })); setDirty(true) }
  // TAB v tele MUSÍ odsadzovať (Mirko 24.7.). Prehliadač si ho inak berie na presun fokusu a kurzor vyskočí z textu.
  // V zozname vnára/vynára položku, v bežnom texte pridá/uberie odsadenie. Mimo tela sa TAB nechytá — tam sa ním chodí po poliach.
  const onCeKey = (e) => {
    if (e.key !== 'Tab') return
    // 2026-07-24: robustne cez Range API (execCommand bol nespoľahlivý) + stopPropagation (fokus-trap modalu Tab inak zožral).
    e.preventDefault(); e.stopPropagation()
    const sel = window.getSelection()
    if (!sel || !sel.rangeCount) return
    let vZozname = false
    try { vZozname = document.queryCommandState('insertUnorderedList') || document.queryCommandState('insertOrderedList') } catch (_) {}
    if (vZozname) { try { document.execCommand(e.shiftKey ? 'outdent' : 'indent') } catch (_) {} ; readCE(); return }
    const r = sel.getRangeAt(0)
    if (e.shiftKey) {
      if (r.collapsed && r.startContainer.nodeType === 3) {
        const t = r.startContainer.textContent || ''
        let n = 0
        while (n < 4 && r.startOffset - n - 1 >= 0 && /[  ]/.test(t[r.startOffset - n - 1])) n++
        if (n > 0) { const rr = document.createRange(); rr.setStart(r.startContainer, r.startOffset - n); rr.setEnd(r.startContainer, r.startOffset); rr.deleteContents() }
      }
    } else {
      r.deleteContents()
      const node = document.createTextNode('    ')
      r.insertNode(node)
      r.setStartAfter(node); r.collapse(true)
      sel.removeAllRanges(); sel.addRange(r)
    }
    readCE()
  }
  const exec = (cmd) => { try { document.execCommand(cmd, false, null) } catch (_) {}; readCE() }
  const FONT_SIZES = [['small', 'Malé'], ['normal', 'Normálne'], ['large', 'Veľké'], ['xlarge', 'Väčšie']]

  // číselníky
  useEffect(() => {
    if (!open) return
    fetchAuth('/api/users').then(r => r.ok ? r.json() : []).then(list => {
      const arr = Array.isArray(list) ? list : []
      setUsers(arr)
      setF(p => p.podpis ? p : { ...p, podpis: (arr.find(u => u.id === user?.id)?.display_name) || user?.display_name || '' })
    }).catch(() => {})
    fetchAuth('/api/projects').then(r => r.ok ? r.json() : []).then(d => setProjects(Array.isArray(d) ? d : (d.projects || d.rows || []))).catch(() => {})
    // ciselniky na predvyplnenie predlohy (Mirko 29.7.)
    fetchAuth('/api/employees').then(r => r.ok ? r.json() : []).then(d => setZamestnanci(Array.isArray(d) ? d : [])).catch(() => {})
    fetchAuth('/api/vehicles').then(r => r.ok ? r.json() : []).then(d => setVozidla(Array.isArray(d) ? d : (d.rows || []))).catch(() => {})
    fetchAuth('/api/customers').then(r => r.ok ? r.json() : []).then(d => setPartners(Array.isArray(d) ? d : (d.customers || d.rows || []))).catch(() => {})
    fetchAuth('/api/doc-archive/firma').then(r => r.ok ? r.json() : null).then(d => setFirma(d)).catch(() => {})
  }, [open])

  // DOKUMENTY_VYTVARANIE_V1: adresát z karty partnera — firma a adresa z buniek, e-mail firmy ako východisko;
  // osoba a jej e-mail sa vyberú v bloku Kontaktná osoba (Mirko 2. 11.: „email nemôže byť pevný načo máme kontakty???“)
  const naplnPartnera = (c) => {
    if (!c) return
    const a = adresaPartnera(c)
    setPartnerId(String(c.id)); setKontaktTel('')
    setF(p => ({ ...p, adr_firma: c.name || '', adr_do_ruk: '', adr_email: c.email || '', adr_ulica: a.ulica, adr_psc_mesto: a.psc_mesto }))
    setDirty(true)
  }
  const pickPartner = (cid) => {
    if (!cid) { setPartnerId(''); setDirty(true); return }
    naplnPartnera(partners.find(p => String(p.id) === String(cid)))
  }

  // načítanie: existujúci dokument ALEBO nový
  useEffect(() => {
    if (!open) return
    setDirty(false); setStrany(null); setNahlad('')
    setVazby([])   // DOKUMENTY_V1
    setPartnerId(''); setPartner0(''); setKontaktTel('')
    if (docId) {
      setId(docId)
      fetchAuth('/api/doc-archive/' + docId).then(r => r.ok ? r.json() : null).then(d => {
        if (!d) return
        setF({
          adr_firma: d.adr_firma || '', adr_do_ruk: d.adr_do_ruk || '', adr_ulica: d.adr_ulica || '', adr_psc_mesto: d.adr_psc_mesto || '', adr_email: d.adr_email || '',
          nasa_znacka: d.nasa_znacka || '', vasa_znacka: d.vasa_znacka || '', miesto: d.miesto || 'Prešov', datum: (d.datum || todayISO()).slice(0, 10), doc_type: d.doc_type || 'List',
          doc_mode: d.doc_mode || 'vec', subject: d.doc_mode === 'title' ? (d.big_title || '') : (d.vec || ''), body: d.body || '', pozdrav: d.pozdrav != null ? d.pozdrav : 'S pozdravom', podpis: d.podpis || '',
          project_id: d.project_id || '', dopyt_id: d.dopyt_id || '', images: d.images || [], font_size: d.font_size || 'normal',
          tpl_key: d.tpl_key || '', tpl_values: (() => { try { return JSON.parse(d.tpl_values || '{}') } catch (_) { return {} } })(),
        })
        // DOKUMENTY_VYTVARANIE_V1: uložené väzby (zamestnanec, vozidlo, partner) — okno ich ukáže a pri uložení pošle celé
        const vz = Array.isArray(d.vazby) ? d.vazby : []
        setVazby(vz.filter(v => v && (v.typ === 'employee' || v.typ === 'vehicle')).map(v => ({ typ: v.typ, ref_id: String(v.ref_id), label: v.label || '' })))
        const cu = vz.find(v => v && v.typ === 'customer'); if (cu) { setPartnerId(String(cu.ref_id)); setPartner0(String(cu.ref_id)) }
        setLocked(!!d.locked); setStatus(d.status || 'draft'); setDocVersion(d.doc_version || 1)
        setPdfUrl(d.pdf_path ? '/uploads/' + d.pdf_path : (d.filename ? '/uploads/' + d.filename : ''))
        requestAnimationFrame(() => setCE(d.body))
      }).catch(() => {})
    } else {
      setId(null); setLocked(false); setStatus('draft'); setDocVersion(1); setPdfUrl('')
      const base = { ...EMPTY(), ...ctxPrefill(context) }
      setF(base)
      requestAnimationFrame(() => setCE(base.body))
      // číslo KAMIR/ROK/NNN pridelí server pri prvom uložení (DOKUMENTY_V1) — náhľad čísla sa už neukazuje, len zavádzal
      // auto-adresát z objednávateľa zákazky
      if (context.projectId) autofillFromProject(context.projectId)
    }
  }, [open, docId])

  // starý dokument bez väzby na partnera: ak sa adresát volá presne ako partner v registri, okno ho spozná raz pri otvorení (polia nemení)
  const zhodaRef = useRef(false)
  useEffect(() => { if (!open) zhodaRef.current = false }, [open, docId])
  useEffect(() => {
    if (!open || zhodaRef.current || !partners.length) return
    if (docId && !f.adr_firma) return   // dokument sa ešte načítava
    zhodaRef.current = true
    if (partnerId || !String(f.adr_firma || '').trim()) return
    const n = bezDiak(f.adr_firma).trim()
    const zhoda = partners.filter(p => bezDiak(p.name).trim() === n)
    if (zhoda.length === 1) { setPartnerId(String(zhoda[0].id)); setPartner0('?') }   // väzba sa zapíše pri prvom uložení
  }, [open, docId, partners.length, f.adr_firma, partnerId])

  const ctxPrefill = (c) => {
    const o = {}
    if (c.projectId) o.project_id = String(c.projectId)
    if (c.dopytId) o.dopyt_id = String(c.dopytId)
    if (c.recipient) o.adr_firma = String(c.recipient).split('\n')[0]
    if (c.vec) { o.subject = c.vec; o.doc_mode = 'vec' }
    if (c.docType) o.doc_type = c.docType
    return o
  }

  async function autofillFromProject(pid) {
    try {
      const p = await fetchAuth('/api/projects/' + encodeURIComponent(pid)).then(r => r.ok ? r.json() : null)
      const cid = p && (p.customerId || p.customer_id); if (!cid) return
      const c = await fetchAuth('/api/customers/' + encodeURIComponent(cid)).then(r => r.ok ? r.json() : null); if (!c) return
      setF(prev => {
        if (prev.adr_firma) return prev
        const a = adresaPartnera(c)
        setPartnerId(String(c.id))
        return { ...prev, adr_firma: c.name || '', adr_do_ruk: '', adr_email: c.email || '', adr_ulica: a.ulica, adr_psc_mesto: a.psc_mesto }
      })
    } catch (_) {}
  }

  const curTpl = f.tpl_key ? VZORY.find(x => x.key === f.tpl_key) : null
  // CHRBTICA V DOKUMENTOCH (Mirko 29.7.): pole predlohy vie, ODKIAL sa udaj berie (`zdroj`),
  // takze ho Matrix natiahne sam — nic sa neprepisuje rucne a nikde nevznikne preklep.
  const zdrojZamestnanec = !!(curTpl && (curTpl.fields || []).some(fl => String(fl.zdroj || '').startsWith('zamestnanec.')))
  const zdrojVozidlo = !!(curTpl && (curTpl.fields || []).some(fl => String(fl.zdroj || '').startsWith('vozidlo.')))
  const pracaZamestnanec = zdrojZamestnanec || zdrojVozidlo
  const payload = useCallback(() => ({
    adr_firma: f.adr_firma, adr_do_ruk: f.adr_do_ruk, adr_ulica: f.adr_ulica, adr_psc_mesto: f.adr_psc_mesto, adr_email: f.adr_email,
    vasa_znacka: f.vasa_znacka, miesto: f.miesto, datum: f.datum, doc_type: f.doc_type,
    doc_mode: f.doc_mode, vec: f.doc_mode === 'vec' ? f.subject : '', big_title: f.doc_mode === 'title' ? f.subject : '',
    body: f.body, pozdrav: f.pozdrav, podpis: f.podpis, project_id: f.project_id || '', dopyt_id: f.dopyt_id || '',
    title: f.subject || (curTpl && curTpl.label) || f.doc_type, images: f.images, font_size: f.font_size,
    tpl_key: f.tpl_key || '', tpl_values: JSON.stringify(f.tpl_values || {}),
    vazby, vazby_nahrad: true,   // DOKUMENTY_V1 + DOKUMENTY_VYTVARANIE_V1: úplný zoznam zamestnancov a vozidiel
    partner_id: (pracaZamestnanec || partnerId === partner0) ? undefined : (partnerId || ''),   // tlačivo zamestnanca ani nezmenený partner väzby na partnera nemenia
  }), [f, vazby, partnerId, partner0, curTpl, pracaZamestnanec])

  // Ulož: prvý raz POST /draft (rezervuje značku), potom PUT. Vráti id.
  async function ensureSaved() {
    if (id) {
      const r = await fetchAuth('/api/doc-archive/' + id, { method: 'PUT', body: JSON.stringify(payload()) })
      if (r.status === 423) { const d = await r.json().catch(() => ({})); throw new Error(d.error || 'Dokument je uzamknutý.') }
      if (!r.ok) throw new Error('Uloženie zlyhalo (' + r.status + ')')
      setPartner0(partnerId)
      return id
    }
    const r = await fetchAuth('/api/doc-archive/draft', { method: 'POST', body: JSON.stringify(payload()) })
    const d = await r.json().catch(() => ({}))
    if (!r.ok || !d.id) throw new Error(d.error || 'Vytvorenie zlyhalo')
    setId(d.id); if (d.nasa_znacka) setF(p => ({ ...p, nasa_znacka: d.nasa_znacka })); setPartner0(partnerId)
    return d.id
  }

  async function doSave() {
    if (!htmlHasText(f.body) && !f.adr_firma.trim()) { toast.info('Doplň aspoň adresáta alebo text'); return }
    setBusy('save')
    try {
      const theId = await ensureSaved(); setDirty(false)
      const ch = chybajuce()
      if (status === 'sent') toast.success('Uložené (odoslané PDF ostáva — nové urobí Tlačiť / PDF)')
      else if (ch.length) toast.info('Uložené. PDF sa urobí, keď doplníš: ' + ch.join(', '))
      else {
        const r = await fetchAuth('/api/doc-archive/' + theId + '/regenerate', { method: 'POST' })
        const d = await r.json().catch(() => ({}))
        if (r.ok && d.url) { setPdfUrl(d.url + '?t=' + Date.now()); if (d.strany) setStrany(d.strany) }
        toast.success(r.ok ? 'Uložené aj s PDF' + (d.strany ? ' (' + d.strany + (d.strany === 1 ? ' strana' : d.strany < 5 ? ' strany' : ' strán') + ')' : '') : 'Uložené (PDF sa nepodarilo: ' + (d.error || r.status) + ')')
      }
      onSaved && onSaved()
    }
    catch (e) { toast.error(e.message) } finally { setBusy(null) }
  }
  // DOKUMENTY_VYTVARANIE_V1 (kontrolór V3): údaj z karty sa nepíše ručne; kým chýba, PDF ani odoslanie nevznikne (ústava 4b, 8.DOK, 111.1)
  const chybajuce = () => {
    if (!curTpl) return []
    const out = []
    if (zdrojZamestnanec && !vazby.some(v => v.typ === 'employee')) out.push('zamestnanec (vyber ho v bloku 2)')
    if (zdrojVozidlo && !vazby.some(v => v.typ === 'vehicle')) out.push('vozidlo (vyber ho v bloku 2)')
    for (const fl of (curTpl.fields || [])) if (fl.zdroj && !fl.nepovinne && !String((f.tpl_values || {})[fl.ph] || '').trim()) out.push(fl.label + ' — doplň na karte')
    if (/\[(názov|adresa|telefón|e-mail) podniku\]/.test(String(f.body || ''))) out.push('údaje podniku v Nastaveniach')
    for (const fl of (curTpl.fields || [])) if (!fl.zdroj && !fl.nepovinne && (String(f.body || '').includes(fl.ph) || String(f.subject || '').includes(fl.ph))) out.push(fl.label)
    return out
  }
  async function doRegenerate() {
    { const ch = chybajuce(); if (ch.length) { toast.error('PDF sa nevyrobí — chýba: ' + ch.join(', ')); return } }
    setBusy('pdf')
    try {
      const theId = await ensureSaved(); setDirty(false)
      const r = await fetchAuth('/api/doc-archive/' + theId + '/regenerate', { method: 'POST' })
      const d = await r.json().catch(() => ({}))
      if (r.status === 423) throw new Error(d.error || 'Uzamknuté')
      if (!r.ok || !d.url) throw new Error(d.error || 'PDF sa nepodarilo vyrobiť')
      setPdfUrl(d.url + '?t=' + Date.now()); if (d.strany) setStrany(d.strany); window.open(d.url, '_blank'); toast.success('PDF pripravené' + (d.strany ? ' — ' + d.strany + (d.strany === 1 ? ' strana' : d.strany < 5 ? ' strany' : ' strán') : ''))
      onSaved && onSaved()
    } catch (e) { toast.error(e.message) } finally { setBusy(null) }
  }
  async function doSend() {
    { const ch = chybajuce(); if (ch.length) { toast.error('Dokument sa neodošle — chýba: ' + ch.join(', ')); return } }
    const to = (f.adr_email || '').trim()
    if (!EMAIL_OK.test(to)) { toast.info('Chýba e-mail adresáta — vyber kontaktnú osobu s e-mailom alebo e-mail firmy v bloku Komu'); return }
    if (!window.confirm('Odoslať dokument na ' + to + '?\nPo odoslaní sa ZAMKNE (úprava len cez Novú verziu).')) return
    setBusy('send')
    try {
      const theId = await ensureSaved(); setDirty(false)
      await fetchAuth('/api/doc-archive/' + theId + '/regenerate', { method: 'POST' })
      const r = await fetchAuth('/api/doc-archive/' + theId + '/send-email', { method: 'POST', body: JSON.stringify({ email: to }) })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(d.error || 'Odoslanie zlyhalo')
      toast.success('Odoslané na ' + to + ' — dokument zamknutý')
      setLocked(true); setStatus('sent'); onSaved && onSaved()
    } catch (e) { toast.error(e.message) } finally { setBusy(null) }
  }
  // DOKUMENTY_DOPLNKY_V1 (Mirko 3. 11. 2026, matrix 5034): list poslaný poštou / odovzdaný osobne — uloží, urobí PDF a zapíše odoslanie (zamkne ako e-mail)
  async function doMimo() {
    if (!mimo || !mimo.sposob) { toast.info('Vyber, ako bol dokument odoslaný'); return }
    if (!String(f.adr_firma || '').trim()) { toast.error('Chýba adresát — doplň ho v bloku Komu'); return }
    if (mimo.sposob !== 'Osobne odovzdané' && !(String(f.adr_ulica || '').trim() && String(f.adr_psc_mesto || '').trim())) { toast.error('Chýba adresa adresáta — bez nej sa list poštou ani kuriérom neposiela'); return }
    setBusy('mimo')
    try {
      const theId = await ensureSaved(); setDirty(false)
      const rg = await fetchAuth('/api/doc-archive/' + theId + '/regenerate', { method: 'POST' })
      const dg = await rg.json().catch(() => ({}))
      if (!rg.ok) throw new Error(dg.error || 'PDF sa nepodarilo vyrobiť')
      if (dg.url) setPdfUrl(dg.url + '?t=' + Date.now()); if (dg.strany) setStrany(dg.strany)
      const r = await fetchAuth('/api/doc-archive/' + theId + '/odoslane-mimo', { method: 'POST', body: JSON.stringify({ sposob: mimo.sposob, datum: mimo.datum }) })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(d.error || 'Nepodarilo sa zapísať')
      toast.success('Zapísané: ' + d.sent_to + (d.zamknute ? ' — dokument zamknutý' : ''))
      setMimo(null); setLocked(!!d.zamknute); setStatus('sent'); onSaved && onSaved()
    } catch (e) { toast.error(e.message) } finally { setBusy(null) }
  }
  async function doAktualizovat() {
    setBusy('ver')
    try {
      const r = await fetchAuth('/api/doc-archive/' + id + '/aktualizovat', { method: 'POST' })
      const d = await r.json().catch(() => ({}))
      if (!r.ok || !d.id) throw new Error(d.error || 'Nepodarilo sa')
      toast.success('Nová verzia v' + d.doc_version); onSaved && onSaved()
      // znovu otvor editor na novej verzii
      setId(d.id); setLocked(false); setStatus('draft'); setDocVersion(d.doc_version); setDirty(false)
      const nd = await fetchAuth('/api/doc-archive/' + d.id).then(x => x.ok ? x.json() : null)
      if (nd) setPdfUrl(nd.pdf_path ? '/uploads/' + nd.pdf_path : '')
    } catch (e) { toast.error(e.message) } finally { setBusy(null) }
  }

  // Predloha — polia žijú TRVALO v editore (sekcia „Údaje predlohy"), telo sa skladá z nich naživo. Mirko 2026-07-23.
  const dateSk = (iso) => { const p = String(iso || '').split('-'); return p.length === 3 ? (+p[2] + '.' + (+p[1]) + '.' + p[0]) : (iso || '') }
  const doplnPolia = (text, v, values) => {
    let out = String(text)
    for (const fl of (v.fields || [])) { const val = String((values || {})[fl.ph] || '').trim(); if (val) out = out.split(fl.ph).join(fl.typ === 'date' ? dateSk(val) : val); else if (fl.nepovinne) out = out.split(fl.ph).join('—') }   // nepovinné prázdne pole = pomlčka
    return out
  }
  const buildTplBody = (v, values) => {
    const konatel = (f.podpis || '').trim() || '[meno a priezvisko konateľa]'
    const fi = firma || {}
    const body = String(v.body).split('[názov podniku]').join(fi.nazov || '[názov podniku]').split('[adresa podniku]').join(fi.adresa || '[adresa podniku]').split('[telefón podniku]').join(fi.telefon || '[telefón podniku]').split('[e-mail podniku]').join(fi.email || '[e-mail podniku]')
      .split('[meno a priezvisko konateľa]').join(konatel).split('[meno konateľa]').join(konatel).split('[miesto]').join(f.miesto || 'Prešov').split('[dátum]').join(dateSk(f.datum))
    return doplnPolia(body, v, values)
  }
  const hodnotaZo = (obj, kluc) => {
    if (!obj) return ''
    if (kluc === 'name') return String(obj.name || [obj.firstName, obj.lastName].filter(Boolean).join(' ') || '').trim()
    if (kluc === 'znacka_model') return [obj.brand, obj.model].filter(Boolean).join(' ') || String(obj.name || '')
    return String(obj[kluc] == null ? '' : obj[kluc])
  }
  const naplnZo = (prefix, obj) => {
    if (!obj || !curTpl) return
    // DOKUMENTY_VYTVARANIE_V1: výber zamestnanca/vozidla z číselníka nahradí predchádzajúci (dokument je o jednom človeku a jeho vozidlách)
    { const typ = prefix === 'zamestnanec' ? 'employee' : 'vehicle'; const label = prefix === 'zamestnanec' ? hodnotaZo(obj, 'name') : [obj.plate, obj.vin].filter(Boolean).join(' ')   // DOKUMENTY_V1
      setVazby(v => [...v.filter(x => x.typ !== typ), { typ, ref_id: String(obj.id), label }]); setDirty(true) }
    const chyba = []
    ;(curTpl.fields || []).forEach(fl => {
      const z = String(fl.zdroj || '')
      if (!z.startsWith(prefix + '.')) return
      const v = hodnotaZo(obj, z.slice(prefix.length + 1))
      setTplVal(fl.ph, v)
      if (!v) chyba.push(fl.label)
    })
    if (chyba.length) (toast.warn || toast.info)('Na karte chýba: ' + chyba.join(', ') + ' — doplň to tam, nech to netreba písať znova.')
  }

  const setTplVal = (ph, val) => { setF(p => ({ ...p, tpl_values: { ...p.tpl_values, [ph]: val } })); setDirty(true) }
  const setTplVals = (o) => { setF(p => ({ ...p, tpl_values: { ...p.tpl_values, ...o } })); setDirty(true) }
  const applyVzor = (key) => {
    if (!key) return
    const v = VZORY.find(x => x.key === key); if (!v) return
    if (f.tpl_key !== key && htmlHasText(f.body) && !window.confirm('Prepísať dokument predlohou „' + v.label + '"?')) return
    const init = {}; (v.fields || []).forEach(fl => { init[fl.ph] = fl.default || '' })
    const preZam = (v.fields || []).some(fl => /^(zamestnanec|vozidlo)\./.test(String(fl.zdroj || '')))   // DOKUMENTY_VYTVARANIE_V1: tlačivo zamestnanca nemá adresáta
    if (preZam) setPartnerId('')
    const konatel = String(v.body || '').includes('[meno a priezvisko konateľa]') ? users.find(u => /konate/i.test(String(u.funkcia || ''))) : null   // čitateľ 4: tlačivo vystavuje konateľ, nie prihlásený office
    setF(p => ({ ...p, ...(preZam ? { adr_firma: '', adr_do_ruk: '', adr_ulica: '', adr_psc_mesto: '', adr_email: '' } : {}), tpl_key: key, tpl_values: init, doc_type: v.docType, doc_mode: v.mode, subject: v.subject, pozdrav: v.pozdrav != null ? v.pozdrav : p.pozdrav, ...(konatel && konatel.display_name ? { podpis: konatel.display_name } : {}) }))
    setDirty(true)
  }
  // DOKUMENTY_VYTVARANIE_V1: druh bez predlohy — predloha sa zruší, napísaný text ostane
  const vyberDruh = (kod) => {
    if (kod === 'plna') { if (dirty && !window.confirm('Rozpísaný dokument sa neuloží. Prejsť na plnú moc?')) return; onPlnaMoc && onPlnaMoc(); return }
    if (kod.startsWith('v:')) { applyVzor(kod.slice(2)); return }
    const typ = kod.slice(2)
    if (f.tpl_key && !window.confirm('Zrušiť predlohu „' + (curTpl ? curTpl.label : '') + '“? Text dokumentu ostane, údaje predlohy sa už nebudú dopĺňať.')) return
    setF(p => ({ ...p, tpl_key: '', tpl_values: {}, doc_type: typ, doc_mode: 'vec', subject: (!p.subject || p.subject === p.doc_type || p.tpl_key) ? (typ === 'List' ? '' : typ) : p.subject, pozdrav: p.pozdrav || 'S pozdravom' }))
    setDirty(true)
  }
  // predloha aktívna → telo (a predmet s údajmi predlohy) sa skladá z polí (+ konateľ/miesto/dátum) naživo
  useEffect(() => {
    if (!open || !f.tpl_key) return
    const v = VZORY.find(x => x.key === f.tpl_key); if (!v) return
    const html = bodyToEditorHtml(buildTplBody(v, f.tpl_values))
    if (ceRef.current) ceRef.current.innerHTML = html
    const maPolia = (v.fields || []).some(fl => String(v.subject || '').includes(fl.ph))
    const subj = maPolia ? doplnPolia(v.subject, v, f.tpl_values) : null
    setF(p => (p.body === html && (subj == null || p.subject === subj)) ? p : { ...p, body: html, subject: subj == null ? p.subject : subj })
  }, [open, f.tpl_key, f.tpl_values, f.podpis, f.miesto, f.datum, firma])
  // kritik 2. kolo: predloha s konateľom z karty zamestnanca/sprievodcu sa nastaví skôr, než prídu používatelia — konateľ sa doplní po ich načítaní
  useEffect(() => {
    if (!open || docId || !curTpl || !users.length || !String(curTpl.body || '').includes('[meno a priezvisko konateľa]')) return
    const k = users.find(u => /konate/i.test(String(u.funkcia || '')))
    if (k && k.display_name && !users.some(u => u.display_name === f.podpis && /konate/i.test(String(u.funkcia || '')))) setF(p => ({ ...p, podpis: k.display_name }))
  }, [open, docId, f.tpl_key, users.length])
  // DOKUMENTY_V1 (Mirko 21. 10. 2026 „nasad dokumenty“): zo sprievodcu / karty zamestnanca príde predloha a zamestnanec —
  // predloha sa nastaví sama a údaje sa načítajú z karty (nič sa neprepisuje ručne); väzba na zamestnanca sa uloží s dokumentom.
  const autoRef = useRef('')
  useEffect(() => {
    if (!open || docId) { autoRef.current = ''; return }
    const kluc = (context.tplKey || '') + '|' + (context.employeeId || '')
    if (kluc === '|' || autoRef.current === kluc) return
    if (context.tplKey && f.tpl_key !== context.tplKey) { applyVzor(context.tplKey); return }
    if (context.employeeId) {
      const zz = zamestnanci.find(x => String(x.id) === String(context.employeeId)); if (!zz) return
      if (curTpl) naplnZo('zamestnanec', zz)
      else setVazby(v => v.some(x => x.typ === 'employee' && x.ref_id === String(zz.id)) ? v : [...v, { typ: 'employee', ref_id: String(zz.id), label: hodnotaZo(zz, 'name') }])
    }
    autoRef.current = kluc
  }, [open, docId, context.tplKey, context.employeeId, f.tpl_key, zamestnanci.length, curTpl])

  // DOKUMENTY_VYTVARANIE_V1: Vrátenie faktúry — došlé faktúry vybraného partnera
  const maDoslaFa = !!(curTpl && (curTpl.fields || []).some(fl => fl.typ === 'dosla_fa'))
  useEffect(() => {
    if (!open || !maDoslaFa || !partnerId || dosleFa.pre === partnerId) return
    setDosleFa({ pre: partnerId, rows: [], chyba: '' })
    fetchAuth('/api/doc-archive/dosle-fa?partner=' + encodeURIComponent(partnerId)).then(r => r.json().then(j => ({ ok: r.ok, j })))
      .then(({ ok, j }) => setDosleFa({ pre: partnerId, rows: ok && Array.isArray(j.faktury) ? j.faktury : [], chyba: ok ? '' : (j.error || 'Faktúry sa nedajú načítať') }))
      .catch(() => setDosleFa({ pre: partnerId, rows: [], chyba: 'Faktúry sa nedajú načítať' }))
  }, [open, maDoslaFa, partnerId])
  const vyberDoslaFa = (fid) => {
    const fa = dosleFa.rows.find(x => String(x.id) === String(fid)); if (!fa) return
    set('vasa_znacka', String(fa.invoice_number || fa.variable_symbol || ''))   // Vaša značka = číslo vrátenej faktúry
    setTplVals({ '[číslo faktúry]': String(fa.invoice_number || fa.variable_symbol || ''), '[dátum vystavenia]': String(fa.date_issued || '').slice(0, 10),
      '[suma faktúry]': (fa.currency && fa.currency !== 'EUR') ? (Number(fa.amount_total || 0).toLocaleString('sk-SK', { minimumFractionDigits: 2 }) + ' ' + fa.currency) : eur(fa.amount_total),
      '[dátum doručenia]': String(fa.date_received || '').slice(0, 10) })
  }

  // DOKUMENTY_VYTVARANIE_V1: náhľad na originálnom hlavičkovom papieri (letterhead_pdf.buildHtml) — po krátkej pauze v písaní
  useEffect(() => {
    if (!open) return
    const ac = new AbortController()
    const t = setTimeout(() => {
      fetchAuth('/api/doc-archive/nahlad', { method: 'POST', body: JSON.stringify({ ...payload(), images: [], nasa_znacka: f.nasa_znacka || 'KAMIR/' + String(f.datum || '').slice(0, 4) + '/…' }), signal: ac.signal })
        .then(r => r.ok ? r.text() : '').then(h => { if (h) setNahlad(h.replace('</head>', '<style>html{background:#fff}body{padding:18mm 22mm 20mm}</style></head>')) }).catch(() => {})
    }, 900)
    return () => { clearTimeout(t); ac.abort() }
  }, [open, f, partnerId])
  const nahladRef = useRef(null)
  const [nahladW, setNahladW] = useState(420)
  useEffect(() => {
    if (!open || !nahladRef.current || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(e => { const w = e[0] && e[0].contentRect.width; if (w) setNahladW(w) })
    ro.observe(nahladRef.current); return () => ro.disconnect()
  }, [open])

  async function uploadImgs(files) {
    if (!files || !files.length) return
    setBusy('img')
    try {
      const added = []
      for (const file of files) { const fd = new FormData(); fd.append('file', file); const r = await fetchAuth('/api/doc-archive/upload-image', { method: 'POST', body: fd }); const j = await r.json(); if (r.ok && j.url) added.push({ url: j.url, caption: '' }) }
      setF(p => ({ ...p, images: [...p.images, ...added] })); setDirty(true)
    } catch (e) { toast.error('Nahranie zlyhalo: ' + e.message) } finally { setBusy(null) }
  }

  const partner = useMemo(() => partners.find(p => String(p.id) === String(partnerId)) || null, [partners, partnerId])
  const zamVybrany = (vazby.find(v => v.typ === 'employee') || {}).ref_id || ''
  const vozVybrane = (vazby.find(v => v.typ === 'vehicle') || {}).ref_id || ''
  const druhVybrany = f.tpl_key ? 'v:' + f.tpl_key : 't:' + f.doc_type
  const druhy = useMemo(() => {
    const q = bezDiak(hladajDruh).trim()
    // kontrolór V4 (0.J): plná moc má JEDNU cestu — sprievodcu s údajmi z kariet a flotilou; ostatné predlohy plnej moci sa v archíve neponúkajú
    const zoz = onPlnaMoc ? DRUHY.map(([sk, l]) => [sk, sk === 'Vozidlá a úrady' ? [['plna', 'Plná moc (vozidlá, flotila, polícia, DI)'], ...l.filter(([k]) => !['v:spl_vozidlo', 'v:spl_di', 'v:spl_vseobecne'].includes(k) || k === druhVybrany)] : l]) : DRUHY
    return zoz.map(([sk, l]) => [sk, l.filter(([k, n]) => !q || bezDiak(n).includes(q) || k === druhVybrany)]).filter(([, l]) => l.length)
  }, [hladajDruh, druhVybrany, onPlnaMoc])
  const projektyMoznosti = useMemo(() => {
    const moje = partnerId ? projects.filter(p => String(p.customerId || p.customer_id || '') === String(partnerId)) : []
    const ostatne = projects.filter(p => !moje.includes(p))
    const m = (p) => ({ value: String(p.id), label: (p.name || p.id) + ' (' + p.id + ')' + (moje.includes(p) ? ' · zákazka partnera' : '') })
    return [{ value: '', label: '— bez zákazky —' }, ...moje.map(m), ...ostatne.map(m)]
  }, [projects, partnerId])
  const emailyFirmy = partner ? [partner.email, partner.email2].map(x => String(x || '').trim()).filter(x => EMAIL_OK.test(x)) : []

  const guardClose = () => { if (dirty) setGDlg(true); else onClose() }
  if (!open) return null
  const half = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACE.md }
  const ro = locked
  const footer = (
    <>
      <span style={{ marginRight: 'auto', fontSize: 12, color: COLORS.muted }}>
        {f.nasa_znacka ? 'Číslo ' + f.nasa_znacka : 'Číslo pridelí Matrix pri prvom uložení'}{strany ? ' · PDF: ' + strany + (strany === 1 ? ' strana' : strany < 5 ? ' strany' : ' strán') : ''}
      </span>
      <BtnGhost onClick={guardClose}>Zavrieť</BtnGhost>
      {ro ? (
        <Btn variant="nav" onClick={doAktualizovat} disabled={!!busy}>{busy === 'ver' ? '…' : 'Nová verzia (Aktualizovať)'}</Btn>
      ) : (<>
        <BtnGhost onClick={doSave} disabled={!!busy}>{busy === 'save' ? 'Ukladám…' : 'Uložiť'}</BtnGhost>
        <BtnGhost onClick={doRegenerate} disabled={!!busy}>{busy === 'pdf' ? 'Robím PDF…' : 'Tlačiť / PDF'}</BtnGhost>
        {!pracaZamestnanec && <BtnGhost onClick={() => { const ch = chybajuce(); if (ch.length) { toast.error('Dokument nie je hotový — chýba: ' + ch.join(', ')); return } setMimo({ sposob: '', datum: todayLocal() }) }} disabled={!!busy}>Odoslané mimo Matrixu</BtnGhost>}
        {!pracaZamestnanec && <Btn variant="add" onClick={doSend} disabled={!!busy}>{busy === 'send' ? 'Posielam…' : 'Odoslať e-mailom'}</Btn>}
      </>)}
    </>
  )
  const title = (docId ? 'Dokument ' + (f.nasa_znacka || '') : 'Nový dokument') + (docVersion > 1 ? ' · v' + docVersion : '') + (ro ? ' · ZAMKNUTÉ' : '')
  const chip = (on) => ({ padding: '4px 10px', borderRadius: 14, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit', border: `1px solid ${on ? COLORS.nav : COLORS.line}`, background: on ? COLORS.navBg : '#fff', color: on ? COLORS.nav : COLORS.text, fontWeight: on ? 700 : 400 })
  const roRiadok = { ...fld, background: COLORS.bgSoft, color: COLORS.text, minHeight: 34 }
  const sk = nahladW / 794
  return (
    <Modal open title={title} onClose={guardClose} maxWidth={1240} footer={footer}>
      <style>{`.dokv-mriezka{display:grid;grid-template-columns:minmax(0,1fr) minmax(300px,440px);gap:16px;align-items:start}
.dokv-nahlad{position:sticky;top:0}
@media (max-width:1000px){.dokv-mriezka{grid-template-columns:minmax(0,1fr)}.dokv-nahlad{position:static}}`}</style>
      {ro && <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#065f46', borderRadius: RADIUS.sm, padding: '8px 12px', marginBottom: SPACE.md, fontSize: 12.5 }}>Dokument bol odoslaný a je uzamknutý. Na úpravu použi <b>Nová verzia</b> — vytvorí kópiu s tou istou značkou, ktorú po úprave znova odošleš.</div>}
      <div className="dokv-mriezka">
      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.md, opacity: ro ? 0.75 : 1, pointerEvents: ro ? 'none' : 'auto', minWidth: 0 }}>

        {/* 1 · ČO VYBAVUJEŠ */}
        <div style={karta}>
          {nadpis(1, 'Čo vybavuješ?')}
          <input value={hladajDruh} onChange={e => setHladajDruh(e.target.value)} placeholder="Hľadaj druh dokumentu… (napr. faktúra, plná moc, výzva)" style={fld} />
          {druhy.map(([skup, l]) => (
            <div key={skup}>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '.04em', margin: '8px 0 4px' }}>{skup}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {l.map(([k, n]) => <button key={k} type="button" aria-pressed={k === druhVybrany} onClick={() => vyberDruh(k)} style={chip(k === druhVybrany)}>{n}</button>)}
              </div>
            </div>
          ))}
          {!druhy.some(([, l]) => l.some(([k]) => k === druhVybrany)) && <div style={{ fontSize: 11.5, color: COLORS.muted, marginTop: 6 }}>Druh tohto dokumentu: <b>{f.doc_type}</b></div>}
        </div>

        {/* 2 · KOMU */}
        {pracaZamestnanec ? (
          <div style={karta}>
            {nadpis(2, zdrojZamestnanec ? 'Ktorý zamestnanec' : 'Ktoré vozidlo', 'z číselníka — údaje sa vezmú z karty')}
            <div style={{ display: 'grid', gridTemplateColumns: zdrojZamestnanec && zdrojVozidlo ? '1fr 1fr' : '1fr', gap: 8 }}>
              {zdrojZamestnanec && (
                <SearchableSelect value={zamVybrany} menuPortal placeholder="Hľadaj zamestnanca…"
                  onChange={v => naplnZo('zamestnanec', zamestnanci.find(x => String(x.id) === String(v)))}
                  options={[...zamestnanci].filter(z => z.active !== 0 && z.active !== '0' || String(z.id) === zamVybrany)
                    .sort((a, b) => String(a.lastName || a.name || '').localeCompare(String(b.lastName || b.name || ''), 'sk'))
                    .map(z => ({ value: String(z.id), label: hodnotaZo(z, 'name') || String(z.id), popis: [z.position || z.pozicia, z.id].filter(Boolean).join(' · ') }))} />
              )}
              {zdrojVozidlo && (
                <SearchableSelect value={vozVybrane} menuPortal placeholder="Hľadaj vozidlo — EČV, značka…"
                  onChange={v => naplnZo('vozidlo', vozidla.find(x => String(x.id) === String(v)))}
                  options={[...vozidla].filter(v => v.active !== 0 && v.active !== '0' || String(v.id) === vozVybrane)
                    .sort((a, b) => String(a.plate || '').localeCompare(String(b.plate || ''), 'sk'))
                    .map(v => ({ value: String(v.id), label: [v.plate, v.brand, v.model].filter(Boolean).join(' · ') || String(v.id) }))} />
              )}
            </div>
            <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 6 }}>Chýbajúci údaj Matrix povie menom poľa — doplň ho na karte, nech ho netreba písať znova.</div>
          </div>
        ) : (
          <div style={karta}>
            {nadpis(2, 'Komu', 'partner z registra')}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <SearchableSelect value={partnerId} onChange={pickPartner} menuPortal emptyLabel="— vyber partnera —"
                  options={partners.map(p => ({ value: String(p.id), label: (p.name || p.id) + (p.ico ? ' · IČO ' + p.ico : ''), popis: [p.partner_type, p.email].filter(Boolean).join(' · ') }))}
                  placeholder="Hľadaj partnera — názov, IČO…" />
              </div>
              <button type="button" onClick={() => setNovyPartner(true)} style={{ ...fbtn, padding: '7px 10px', borderStyle: 'dashed', color: COLORS.add, fontWeight: 700, whiteSpace: 'nowrap' }}>+ Nový partner</button>
            </div>
            {partner ? (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
                  <div style={{ gridColumn: '1 / -1' }}><span style={lbl}>Firma</span><div style={roRiadok}>{f.adr_firma || partner.name}</div></div>
                  <div><span style={lbl}>Ulica a číslo</span><div style={roRiadok}>{f.adr_ulica || '—'}</div></div>
                  <div><span style={lbl}>PSČ a obec</span><div style={roRiadok}>{f.adr_psc_mesto || '—'}</div></div>
                </div>
                <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 4 }}>Firma a adresa sú z karty partnera{(() => { const a = adresaPartnera(partner); return (!a.ulica || !a.psc_mesto) ? ' — adresa na karte nie je celá, doplň ju v Partneroch' : ((a.ulica !== f.adr_ulica || a.psc_mesto !== f.adr_psc_mesto) ? ' — na karte je teraz iná adresa' : '') })()}. <span onClick={() => naplnPartnera(partner)} style={{ color: COLORS.nav, cursor: 'pointer', textDecoration: 'underline' }}>Načítať znova z karty</span></div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8, marginTop: 10 }}>
                  <KontaktnaOsoba customerId={partnerId} inp={fld}
                    osoba={{ meno: f.adr_do_ruk, email: f.adr_do_ruk ? f.adr_email : '', telefon: kontaktTel }}
                    popisy={{ osoba: 'Do rúk (kontaktná osoba)', email: 'E-mail adresáta' }}
                    obal={(t, c, k) => (k === 'ko-email' && !f.adr_do_ruk)
                      ? <label key={k} style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 700, color: COLORS.muted, minWidth: 0 }}>E-mail adresáta<input type="email" value={f.adr_email || ''} onChange={e => set('adr_email', e.target.value)} placeholder="vyber osobu alebo e-mail firmy" style={fld} /></label>
                      : <label key={k} style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 700, color: COLORS.muted, minWidth: 0 }}>{t}{c}</label>}
                    onZmena={o => { setF(p => ({ ...p, adr_do_ruk: o.meno || '', adr_email: o.meno ? (o.email || '') : (o.email || (emailyFirmy[0] || '')) })); setKontaktTel(o.telefon || ''); setDirty(true) }} />
                </div>
                <div style={{ fontSize: 12, marginTop: 8 }}><span style={{ color: COLORS.muted }}>Pošle sa na: </span><b>{EMAIL_OK.test(String(f.adr_email || '').trim()) ? f.adr_email : '—'}</b>{f.adr_do_ruk ? <span style={{ color: COLORS.muted }}> (osoba {f.adr_do_ruk})</span> : (f.adr_email ? <span style={{ color: COLORS.muted }}> (e-mail firmy)</span> : null)}</div>
                {emailyFirmy.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginTop: 6 }}>
                    <span style={{ fontSize: 11, color: COLORS.muted }}>E-mail firmy (bez osoby):</span>
                    {emailyFirmy.map(e => <button key={e} type="button" onClick={() => { setF(p => ({ ...p, adr_do_ruk: '', adr_email: e })); setKontaktTel(''); setDirty(true) }} style={chip(!f.adr_do_ruk && String(f.adr_email || '').toLowerCase() === e.toLowerCase())}>{e}</button>)}
                  </div>
                )}
                {!EMAIL_OK.test(String(f.adr_email || '').trim()) && <div style={{ fontSize: 11.5, color: COLORS.remove || '#B91C1C', marginTop: 6 }}>E-mail adresáta chýba — vyber osobu s e-mailom, e-mail firmy alebo pridaj novú osobu partnera. Bez e-mailu sa dá dokument len vytlačiť.</div>}
              </>
            ) : (
              <>
                <div style={{ fontSize: 11.5, color: COLORS.muted, margin: '8px 0 6px' }}>Adresát nie je v partneroch? Pridaj ho tlačidlom <b>+ Nový partner</b> — uloží sa do registra a list bude aj na jeho karte.{(f.adr_firma || f.adr_ulica) ? ' Tento dokument má adresáta napísaného ručne:' : ''}</div>
                {(f.adr_firma || f.adr_ulica || f.adr_email) && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <Input value={f.adr_firma} onChange={v => set('adr_firma', v)} placeholder="Firma / meno" fullWidth />
                    <div style={half}>
                      <Input value={f.adr_do_ruk} onChange={v => set('adr_do_ruk', v)} placeholder="Do rúk" fullWidth />
                      <Input value={f.adr_email} onChange={v => set('adr_email', v)} placeholder="E-mail adresáta" fullWidth />
                    </div>
                    <div style={half}>
                      <Input value={f.adr_ulica} onChange={v => set('adr_ulica', v)} placeholder="Ulica a číslo" fullWidth />
                      <Input value={f.adr_psc_mesto} onChange={v => set('adr_psc_mesto', v)} placeholder="PSČ a obec" fullWidth />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* 3 · ZÁKAZKA */}
        {!context.projectId && !pracaZamestnanec && <div style={karta}>
          {nadpis(3, 'Zákazka', '(voliteľné — dá sa zmeniť aj pri úprave)')}
          <SearchableSelect value={f.project_id} onChange={v => set('project_id', v)} menuPortal options={projektyMoznosti} placeholder="vyber zákazku alebo nechaj prázdne…" />
          {partnerId && <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 4 }}>Zákazky tohto partnera sú v zozname hore.</div>}
        </div>}

        {/* 4 · OBSAH */}
        <div style={karta}>
          {nadpis(pracaZamestnanec || context.projectId ? 3 : 4, 'Obsah')}
          {curTpl && curTpl.fields && curTpl.fields.length > 0 && (
            <div style={{ background: '#f0f7ff', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, padding: '10px 12px', marginBottom: 10 }}>
              <span style={{ ...lbl, marginBottom: 2 }}>Údaje predlohy — {curTpl.label}</span>
              <div style={{ fontSize: 11, color: COLORS.muted, marginBottom: 8 }}>Text dokumentu sa poskladá naživo. Meno konateľa (z „Podpisuje"), miesto a dátum sa doplnia samy.</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                {curTpl.fields.map(fl => (
                  <div key={fl.ph}>
                    <span style={{ ...lbl, marginBottom: 2 }}>{fl.label}{fl.zdroj ? <span style={{ fontWeight: 400 }}> · z karty</span> : null}</span>
                    {fl.typ === 'textarea'
                      ? <textarea value={f.tpl_values[fl.ph] || ''} placeholder={fl.hint || ''} onChange={e => setTplVal(fl.ph, e.target.value)} rows={2} style={{ ...fld, resize: 'vertical' }} />
                      : fl.typ === 'select'
                        ? (() => {
                            // Predpisane moznosti + vlastny text. Do dokumentu ide LEN vybrana (Mirko 29.7.).
                            const cur = f.tpl_values[fl.ph] || ''
                            const zoz = fl.opts || []
                            const vlastne = cur.trim() !== '' && !zoz.includes(cur)
                            const prazdneVlastne = cur !== '' && cur.trim() === ''
                            return (
                              <>
                                <select value={(vlastne || prazdneVlastne) ? '__ine__' : cur}
                                  onChange={e => setTplVal(fl.ph, e.target.value === '__ine__' ? ' ' : e.target.value)}
                                  style={fld}>
                                  <option value="">— vyber možnosť —</option>
                                  {zoz.map(o => <option key={o} value={o}>{o}</option>)}
                                  <option value="__ine__">iné — vlastný text</option>
                                </select>
                                {(vlastne || prazdneVlastne) && (
                                  <input value={cur.trim()} placeholder="napíš vlastný text"
                                    onChange={e => setTplVal(fl.ph, e.target.value || ' ')}
                                    style={{ ...fld, marginTop: 6 }} />
                                )}
                              </>
                            )
                          })()
                        : fl.typ === 'cas_datum'
                          ? (() => {
                              // DOKUMENTY_VYTVARANIE_V1: hodiny, minúty a dátum v samostatných poliach; do dokumentu ide „HH:MM / DD.MM.RRRR“
                              const tv = f.tpl_values
                              const st = casDatumRozloz(tv[fl.ph]) || {}
                              const h = tv[fl.ph + '|h'] != null ? tv[fl.ph + '|h'] : (st.h || '')
                              const m = tv[fl.ph + '|m'] != null ? tv[fl.ph + '|m'] : (st.m || '')
                              const d = tv[fl.ph + '|d'] != null ? tv[fl.ph + '|d'] : (st.d || '')
                              const zmen = (nh, nm, nd) => { const ch = casDatumChyba(nh, nm); setTplVals({ [fl.ph + '|h']: nh, [fl.ph + '|m']: nm, [fl.ph + '|d']: nd, [fl.ph]: ch ? '' : casDatumZloz(nh, nm, nd) }) }
                              const chyba = casDatumChyba(h, m)
                              const cislo = { ...fld, width: 58, textAlign: 'center' }
                              return (
                                <div>
                                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <input inputMode="numeric" aria-label={fl.label + ' — hodiny'} placeholder="hh" maxLength={2} value={h} onChange={e => zmen(e.target.value.replace(/\D/g, '').slice(0, 2), m, d)} style={cislo} />
                                    <b>:</b>
                                    <input inputMode="numeric" aria-label={fl.label + ' — minúty'} placeholder="mm" maxLength={2} value={m} onChange={e => zmen(h, e.target.value.replace(/\D/g, '').slice(0, 2), d)} onBlur={() => { if (m.length === 1) zmen(h, '0' + m, d) }} style={cislo} />
                                    <input type="date" aria-label={fl.label + ' — dátum'} value={d} onChange={e => zmen(h, m, e.target.value)} style={{ ...fld, width: 170 }} />
                                    {f.tpl_values[fl.ph] && <span style={{ fontSize: 12, color: COLORS.muted }}>→ {f.tpl_values[fl.ph]}</span>}
                                  </div>
                                  {chyba && <div style={{ fontSize: 11, color: COLORS.remove || '#B91C1C', marginTop: 2 }}>{chyba}</div>}
                                </div>
                              )
                            })()
                          : fl.typ === 'dosla_fa'
                            ? (
                              <div>
                                {!partnerId
                                  ? <div style={{ fontSize: 11.5, color: COLORS.muted }}>Najprv vyber v bloku Komu partnera (dodávateľa) — ponúknu sa jeho došlé faktúry.</div>
                                  : dosleFa.chyba
                                    ? <div style={{ fontSize: 11.5, color: COLORS.remove || '#B91C1C' }}>{dosleFa.chyba}</div>
                                    : <SearchableSelect value="" menuPortal onChange={vyberDoslaFa}
                                        placeholder={dosleFa.rows.length ? 'Vyber faktúru — číslo, dátum, suma…' : 'Tento partner nemá v Matrixe došlé faktúry'}
                                        options={dosleFa.rows.map(x => ({ value: String(x.id), label: (x.invoice_number || x.variable_symbol || x.id) + ' · ' + dateSk(String(x.date_issued || '').slice(0, 10)) + ' · ' + eur(x.amount_total), popis: [x.supplier_name, x.status].filter(Boolean).join(' · ') }))} />}
                                <input value={f.tpl_values[fl.ph] || ''} placeholder="číslo faktúry" onChange={e => setTplVal(fl.ph, e.target.value)} style={{ ...fld, marginTop: 6 }} />
                              </div>
                            )
                            : fl.zdroj
                              ? <div style={{ ...roRiadok, color: f.tpl_values[fl.ph] ? COLORS.text : (COLORS.remove || '#B91C1C') }}>{f.tpl_values[fl.ph] ? (fl.typ === 'date' ? dateSk(f.tpl_values[fl.ph]) : f.tpl_values[fl.ph]) : (pracaZamestnanec && !vazby.length ? 'vyber v bloku 2' : 'chýba na karte — doplň ho tam')}</div>
                              : <input type={fl.typ === 'date' ? 'date' : (fl.typ === 'number' ? 'number' : 'text')} value={f.tpl_values[fl.ph] || ''} placeholder={fl.hint || ''} onChange={e => setTplVal(fl.ph, e.target.value)} style={fld} />}
                    {fl.hint && fl.typ !== 'cas_datum' && <div style={{ fontSize: 10, color: COLORS.muted, marginTop: 1 }}>{fl.hint}</div>}
                    {fl.hint && fl.typ === 'cas_datum' && <div style={{ fontSize: 10, color: COLORS.muted, marginTop: 1 }}>{fl.hint}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {!(curTpl && !String(curTpl.subject || '').trim() && !f.subject) && <div style={{ marginBottom: 10 }}>
            <span style={lbl}>Predmet</span>
            <div style={{ display: 'flex', gap: SPACE.md, marginBottom: 6 }}>
              <label style={{ fontSize: 12, display: 'flex', gap: 4, alignItems: 'center', cursor: 'pointer' }}><input type="radio" checked={f.doc_mode === 'vec'} onChange={() => set('doc_mode', 'vec')} /> Vec: …</label>
              <label style={{ fontSize: 12, display: 'flex', gap: 4, alignItems: 'center', cursor: 'pointer' }}><input type="radio" checked={f.doc_mode === 'title'} onChange={() => set('doc_mode', 'title')} /> Veľký nadpis</label>
            </div>
            <Input value={f.subject} onChange={v => set('subject', v)} placeholder={f.doc_mode === 'title' ? 'OZNÁMENIE' : 'Oznámenie o stave realizácie diela'} fullWidth />
          </div>}
          <div>
            <span style={lbl}>Text dokumentu <span style={{ fontWeight: 400, color: COLORS.muted }}>(označ text a naformátuj — zmena je vidieť hneď)</span></span>
            <div style={{ display: 'flex', gap: 6, margin: '4px 0 6px', flexWrap: 'wrap', alignItems: 'center' }}>
              {[['bold', 'B', { fontWeight: 800 }], ['italic', 'I', { fontStyle: 'italic' }], ['underline', 'U', { textDecoration: 'underline' }]].map(([cmd, lab, st]) => (
                <button key={cmd} type="button" title={lab} onMouseDown={e => e.preventDefault()} onClick={() => exec(cmd)} style={{ ...fbtn, ...st, minWidth: 30 }}>{lab}</button>
              ))}
              <span style={{ width: 1, height: 18, background: COLORS.line }} />
              <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => exec('insertUnorderedList')} style={fbtn}>• Odrážky</button>
              <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => exec('insertOrderedList')} style={fbtn}>1. Číslovať</button>
              <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => exec('removeFormat')} style={fbtn}>Zrušiť formát</button>
              <span style={{ width: 1, height: 18, background: COLORS.line }} />
              <span style={{ fontSize: 11, color: COLORS.muted }}>Veľkosť:</span>
              <select value={f.font_size} onChange={e => set('font_size', e.target.value)} style={{ ...fbtn, cursor: 'pointer' }}>
                {FONT_SIZES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div ref={ceRef} contentEditable={!ro} suppressContentEditableWarning
              onInput={readCE} onBlur={readCE} onKeyDown={onCeKey}
              style={{ ...fld, minHeight: 200, maxHeight: 360, overflowY: 'auto', lineHeight: 1.55, textAlign: 'left', cursor: ro ? 'default' : 'text' }} />
          </div>
          <div style={{ marginTop: 10 }}>
            <span style={lbl}>Obrázky <span style={{ fontWeight: 400, color: COLORS.muted }}>(príloha, 4 na stranu)</span></span>
            <label style={{ ...fld, width: 'auto', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
              {busy === 'img' ? 'Nahrávam…' : '+ Z počítača'}
              <input type="file" accept="image/*" multiple onChange={e => uploadImgs(e.target.files)} style={{ display: 'none' }} />
            </label>
            {f.images.length > 0 && <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
              {f.images.map((im, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <img src={im.url} alt="" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: RADIUS.sm, border: `1px solid ${COLORS.line}` }} />
                  <input value={im.caption} onChange={e => { setF(p => ({ ...p, images: p.images.map((x, j) => j === i ? { ...x, caption: e.target.value } : x) })); setDirty(true) }} placeholder="Popis fotky…" style={{ ...fld, flex: 1 }} />
                  <span onClick={() => { setF(p => ({ ...p, images: p.images.filter((_, j) => j !== i) })); setDirty(true) }} style={{ cursor: 'pointer', color: COLORS.brand, fontWeight: 700, padding: '0 6px' }}>✕</span>
                </div>
              ))}
            </div>}
          </div>
          <div style={{ marginTop: 10 }}><span style={lbl}>Pozdrav</span><Input value={f.pozdrav} onChange={v => set('pozdrav', v)} placeholder="S pozdravom" fullWidth /></div>
          <details style={{ marginTop: 10 }}>
            <summary style={{ cursor: 'pointer', color: COLORS.nav, fontWeight: 600, fontSize: 12 }}>Značky, miesto, dátum, podpis</summary>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
              <div style={half}>
                <div><span style={lbl}>Naša značka</span><div style={{ ...roRiadok, color: f.nasa_znacka ? COLORS.text : COLORS.muted }}>{f.nasa_znacka || 'pridelí Matrix pri uložení'}</div></div>
                <div><span style={lbl}>Vaša značka</span><Input value={f.vasa_znacka} onChange={v => set('vasa_znacka', v)} placeholder="—" fullWidth /></div>
              </div>
              <div style={half}>
                <div><span style={lbl}>Miesto</span><Input value={f.miesto} onChange={v => set('miesto', v)} fullWidth /></div>
                <div><span style={lbl}>Dátum</span><input type="date" value={f.datum} onChange={e => set('datum', e.target.value)} style={fld} /></div>
              </div>
              <div><span style={lbl}>Podpisuje</span>
                <select value={f.podpis} onChange={e => set('podpis', e.target.value)} style={fld}>
                  {!users.some(u => u.display_name === f.podpis) && f.podpis && <option value={f.podpis}>{f.podpis}</option>}
                  {users.map(u => <option key={u.id} value={u.display_name}>{(u.cele_meno || u.display_name) + (u.funkcia ? ' – ' + u.funkcia : '')}</option>)}
                </select></div>
            </div>
          </details>
        </div>
      </div>

      {/* NÁHĽAD — originálny hlavičkový papier */}
      <div className="dokv-nahlad">
        <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>Náhľad papiera</div>
        <div ref={nahladRef} style={{ width: '100%', height: Math.round(1123 * sk), overflow: 'hidden', background: '#fff', boxShadow: '0 1px 6px rgba(15,23,42,.18)', borderRadius: 2 }}>
          {nahlad
            ? <iframe title="Náhľad dokumentu" sandbox="" srcDoc={nahlad} style={{ width: 794, height: 1123, border: 0, transform: 'scale(' + sk + ')', transformOrigin: '0 0', display: 'block' }} />
            : <div style={{ padding: 20, fontSize: 12, color: COLORS.muted }}>Náhľad sa pripravuje…</div>}
        </div>
        <div style={{ fontSize: 10.5, color: COLORS.muted, marginTop: 6, lineHeight: 1.45 }}>Ten istý papier, aký vyrobí PDF. Pätička (MATRIX, strany, údaje firmy) a zalomenie strán sú až v PDF — počet strán ukáže <b>Tlačiť / PDF</b>.</div>
      </div>
      </div>
      {novyPartner && <NoviCustomerDialog open onClose={() => setNovyPartner(false)}
        onCreated={(d) => { if (d && d.id) { setPartners(p => [d, ...p.filter(x => String(x.id) !== String(d.id))]); naplnPartnera(d) } }}
        onExistujuci={(c) => { if (c && c.id) { setPartners(p => p.some(x => String(x.id) === String(c.id)) ? p : [c, ...p]); naplnPartnera(c) } }} />}
      {mimo && <div onClick={() => busy !== 'mimo' && setMimo(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: RADIUS.md, padding: 20, width: 'min(420px,92vw)', boxShadow: '0 20px 60px rgba(0,0,0,.3)' }}>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Odoslané mimo Matrixu</div>
          <div style={{ fontSize: 12, color: COLORS.muted, marginBottom: 12 }}>{(f.nasa_znacka || 'Dokument') + (f.adr_firma ? ' · ' + f.adr_firma : '')}. Zapíš, až keď dokument naozaj odišiel — zápis sa nedá vrátiť. List sa potom zamkne a mení sa už len cez Novú verziu (zmluvy a tlačivá sa nezamykajú).</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
            {['Poštou', 'Doporučene poštou', 'Kuriérom', 'Osobne odovzdané'].map(s => <span key={s} onClick={() => setMimo(m => ({ ...m, sposob: s }))} style={chip(mimo.sposob === s)}>{s}</span>)}
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.muted, marginBottom: 3 }}>Dátum odoslania</div>
          <input type="date" value={mimo.datum} max={todayLocal()} onChange={e => setMimo(m => ({ ...m, datum: e.target.value }))} style={{ ...fld, width: 170, marginBottom: 14 }} />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <BtnGhost onClick={() => setMimo(null)} disabled={busy === 'mimo'}>Zrušiť</BtnGhost>
            <Btn variant="add" onClick={doMimo} disabled={!!busy || !mimo.sposob}>{busy === 'mimo' ? 'Zapisujem…' : 'Zapísať odoslanie'}</Btn>
          </div>
        </div>
      </div>}
      <Modal.DirtyConfirm open={gDlg} message="Rozpísaný dokument — ulož ho tlačidlom Uložiť, alebo zahoď zmeny."
        onSave={() => setGDlg(false)} onContinue={() => setGDlg(false)} onDiscard={() => { setGDlg(false); onClose() }} />
    </Modal>
  )
}
